"use client"

import { useState, useRef, useCallback } from "react"
import { motion } from "framer-motion"
import { SlidesIcon, PitchDeckIcon, CapTableIcon, DocumentsIcon, ResearchIcon } from "./deck-type-icons"
import { Crown } from "lucide-react"
import Tooltip from "@mui/material/Tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { useGetCartCreditStatusQuery } from "@/app/store/api/cartApi"
import { useAppSelector } from "@/app/store/hooks"

export type DeckType =
    | "Presentation"
    | "documents"
    | "fundraising-pitch-deck"
    | "business-pitch-deck"
    | "deep-research"

const DOCUMENT_SUBTYPES = [
    {
        label: "Custom",
        value: "custom",
        tooltip: "Create custom documents",
    },
    {
        label: "Partnership Proposal",
        value: "partnership-proposal",
        tooltip: "Draft partnership agreements & joint venture proposals",
    },
    {
        label: "NDA",
        value: "NDA",
        tooltip: "Generate non-disclosure & confidentiality agreements",
    },
    {
        label: "Resume",
        value: "resume",
        tooltip: "Create professional resumes & CVs",
    },
    {
        label: "Business Document",
        value: "business-document",
        tooltip: "Create general business documents & reports",
    },
    {
        label: "Service Agreement",
        value: "service-agreement",
        tooltip: "Draft service-level agreements & contracts",
    },
]

const PITCH_DECK_SUBTYPES = [
    {
        label: "Fundraising Pitch",
        value: "fundraising-pitch-deck",
        tooltip: "Investor-ready pitch decks for raising capital",
    },
    {
        label: "Business Pitch",
        value: "business-pitch-deck",
        tooltip: "Professional decks for sales & partnerships",
    },
]

export const TOOLTIP_SLOT_PROPS = {
    tooltip: {
        sx: {
            bgcolor: "rgba(15, 23, 42, 0.95)",
            borderRadius: "10px",
            px: 1.5,
            py: 1,
            fontSize: "12px",
            lineHeight: 1.5,
            boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
            "& .MuiTooltip-arrow": {
                color: "rgba(15, 23, 42, 0.95)",
            },
        },
    },
}

const MENU_TOOLTIP_PROPS = {
    disableFocusListener: true,
    disableTouchListener: true,
    enterDelay: 300,
}

const CHIPS = [
    {
        type: "Presentation" as DeckType,
        label: "Presentation",
        icon: SlidesIcon,
        iconColor: "text-gray-700",
        activeIconColor: "text-gray-800",
        activeTextColor: "text-gray-800",
        underlineColor: "decoration-gray-500",
        tooltip: (
            <div className="space-y-1 py-1 text-xs">
                <p>&#8226; Create presentations from scratch</p>
                <p>&#8226; AI-powered slide generation</p>
                <p>&#8226; 16:9 landscape format</p>
            </div>
        ),
    },
    {
        type: "fundraising-pitch-deck" as DeckType,
        label: "Pitch Deck",
        icon: PitchDeckIcon,
        iconColor: "text-gray-700",
        activeIconColor: "text-gray-700",
        activeTextColor: "text-gray-700",
        underlineColor: "decoration-gray-500",
        tooltip: null,
    },


    {
        type: "documents" as DeckType,
        label: "Documents",
        icon: DocumentsIcon,
        iconColor: "text-gray-700",
        activeIconColor: "text-gray-700",
        activeTextColor: "text-gray-700",
        underlineColor: "decoration-gray-500",
        tooltip: null,
    },
    {
        type: "deep-research" as DeckType,
        label: "Research",
        icon: ResearchIcon,
        iconColor: "text-gray-700",
        activeIconColor: "text-gray-700",
        activeTextColor: "text-gray-700",
        underlineColor: "decoration-gray-500",
        tooltip: (
            <div className="space-y-1 py-1 text-xs">
                <p>&#8226; Deep research on any topic</p>
                <p>&#8226; Multi-source analysis & citations</p>
                <p>&#8226; Professional Word document output</p>
            </div>
        ),
    },
]

interface DeckTypeChipsProps {
    selectedDeckType: DeckType
    onSelectDeckType: (type: DeckType) => void
    onSelectDocumentSubtype: (subtype: string) => void
}

export function DeckTypeChips({
    selectedDeckType,
    onSelectDeckType,
    onSelectDocumentSubtype,
}: DeckTypeChipsProps) {
    const { isAuthenticated } = useAppSelector((state) => state.auth)
    const { data: cartStatusResponse } = useGetCartCreditStatusQuery(undefined, { skip: !isAuthenticated })
    const credits = cartStatusResponse?.data?.credits

    // Consolidated remaining counts (Free + Purchased - Used)
    const proRemaining = (credits?.proDecks?.freeRemaining ?? 0) + ((credits?.proDecks?.purchased ?? 0) - (credits?.proDecks?.used ?? 0));
    const fastRemaining = (credits?.fastDecks?.freeRemaining ?? 0) + ((credits?.fastDecks?.purchased ?? 0) - (credits?.fastDecks?.used ?? 0));
    const pitchRemaining = (credits?.fundraisingDecks?.freeRemaining ?? 0) + ((credits?.fundraisingDecks?.purchased ?? 0) - (credits?.fundraisingDecks?.used ?? 0));
    const docsRemaining = (credits?.documents?.freeRemaining ?? 0) + ((credits?.documents?.purchased ?? 0) - (credits?.documents?.used ?? 0));
    const researchRemaining = (credits?.marketResearch?.freeRemaining ?? 0) + ((credits?.marketResearch?.purchased ?? 0) - (credits?.marketResearch?.used ?? 0));

    const [docPopoverOpen, setDocPopoverOpen] = useState(false)
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const [pitchPopoverOpen, setPitchPopoverOpen] = useState(false)
    const pitchCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const handleMouseEnter = useCallback(() => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current)
            closeTimeoutRef.current = null
        }
        setDocPopoverOpen(true)
    }, [])

    const handleMouseLeave = useCallback(() => {
        closeTimeoutRef.current = setTimeout(() => {
            setDocPopoverOpen(false)
        }, 200)
    }, [])

    const handleDocSubtypeClick = (subtype: string) => {
        onSelectDeckType("documents")
        onSelectDocumentSubtype(subtype)
        setDocPopoverOpen(false)
    }

    const handlePitchMouseEnter = useCallback(() => {
        if (pitchCloseTimeoutRef.current) {
            clearTimeout(pitchCloseTimeoutRef.current)
            pitchCloseTimeoutRef.current = null
        }
        setPitchPopoverOpen(true)
    }, [])

    const handlePitchMouseLeave = useCallback(() => {
        pitchCloseTimeoutRef.current = setTimeout(() => {
            setPitchPopoverOpen(false)
        }, 200)
    }, [])

    const handlePitchSubtypeClick = (type: DeckType) => {
        onSelectDeckType(type)
        setPitchPopoverOpen(false)
    }

    const renderChip = (chip: (typeof CHIPS)[number], index: number) => {
        const Icon = chip.icon
        const isPitchDeck = chip.type === "fundraising-pitch-deck"
        const isSelected = selectedDeckType === chip.type || (isPitchDeck && selectedDeckType === "business-pitch-deck")
        const isDocuments = chip.type === "documents"
        const isDropdown = isDocuments || isPitchDeck

        const buttonEl = (
            <motion.button
                type="button"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                    duration: 0.3,
                    delay: 0.15 + index * 0.06,
                }}
                onClick={(e) => {
                    if (isDocuments) {
                        e.preventDefault()
                        setDocPopoverOpen(true)
                        return
                    }
                    if (isPitchDeck) {
                        e.preventDefault()
                        setPitchPopoverOpen(true)
                        return
                    }
                    onSelectDeckType(chip.type)
                }}
                onPointerDown={(e) => {
                    if (isDropdown) {
                        e.preventDefault()
                    }
                }}
                className={cn(
                    "flex min-w-[88px] flex-col items-center justify-start gap-2 group p-2 sm:p-3 rounded-2xl transition-all duration-200",
                    isDropdown ? "cursor-pointer" : "cursor-pointer",
                )}
            >
                <div
                    className={cn(
                        "relative flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-xl sm:rounded-full shadow-md transition-all duration-200",
                        isSelected
                            ? "bg-white border border-slate-800 shadow-[0_2px_12px_rgba(0,0,0,0.12)] scale-105"
                            : "bg-white border border-slate-100 group-hover:scale-105 group-hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)] hover:border-slate-300"
                    )}
                >
                    <Icon className={cn("h-4 w-4 sm:h-5 sm:w-5 transition-colors duration-200", isSelected ? chip.activeIconColor : chip.iconColor, "group-hover:" + chip.activeIconColor)} strokeWidth={1.8} />

                    {/* Crown Badge */}
                    {chip.type === "fundraising-pitch-deck" && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[9px] font-semibold px-2 bg-gradient-to-r from-violet-600 via-fuchsia-500 to-pink-500 text-white p-0.5 rounded-full shadow-sm border border-white">
                            New
                        </div>
                    )}
                </div>
                <span
                    className={cn(
                        "text-[10px] sm:text-[12px] font-medium text-center leading-tight transition-all duration-200 break-words",
                        isSelected
                            ? cn("font-semibold", chip.activeTextColor, chip.underlineColor)
                            : "text-slate-500 group-hover:text-slate-700"
                    )}
                >
                    {chip.label}
                </span>
            </motion.button>
        )

        let tooltipContent = chip.tooltip;

        if (tooltipContent) {
            let statsContent = null;

            if (chip.type === "Presentation" && credits) {
                statsContent = (
                    <>
                        <div className="flex justify-between items-center whitespace-nowrap gap-4">
                            <span className="text-[13px] text-slate-300 tracking-wide">Pro Decks Remaining:</span>
                            <span className="font-semibold text-[14px] text-white">{proRemaining}</span>
                        </div>
                        <div className="flex justify-between items-center whitespace-nowrap gap-4">
                            <span className="text-[13px] text-slate-300 tracking-wide">Fast Decks Remaining:</span>
                            <span className="font-semibold text-[14px] text-white">{fastRemaining}</span>
                        </div>
                    </>
                );
            } else if (chip.type === "fundraising-pitch-deck" && credits) {
                statsContent = (
                    <div className="flex justify-between items-center whitespace-nowrap gap-4">
                        <span className="text-[13px] text-slate-300 tracking-wide">Pitch Decks Remaining:</span>
                        <span className="font-semibold text-[14px] text-white">{pitchRemaining}</span>
                    </div>
                );
            } else if (chip.type === "deep-research" && credits) {
                statsContent = (
                    <div className="flex justify-between items-center whitespace-nowrap gap-4">
                        <span className="text-[13px] text-slate-300 tracking-wide">Research Remaining:</span>
                        <span className="font-semibold text-[14px] text-white">{researchRemaining}</span>
                    </div>
                );
            }

            if (statsContent) {
                tooltipContent = (
                    <div className="flex flex-col">
                        {chip.tooltip}
                        <div className="mt-2 pt-2 border-t border-slate-700/50 flex flex-col gap-1.5">
                            {statsContent}
                        </div>
                    </div>
                );
            }
        }

        const chipWithTooltip = tooltipContent ? (
            <Tooltip
                key={chip.type}
                title={tooltipContent}
                placement="bottom"
                arrow
                slotProps={TOOLTIP_SLOT_PROPS}
            >
                {buttonEl}
            </Tooltip>
        ) : (
            buttonEl
        )

        if (isDocuments) {
            return (
                <Popover
                    key={chip.type}
                    open={docPopoverOpen}
                    onOpenChange={setDocPopoverOpen}
                >
                    <div
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        <PopoverTrigger asChild>
                            {chipWithTooltip}
                        </PopoverTrigger>
                        <PopoverContent
                            className="w-56 p-1 shadow-xl border-slate-200"
                            sideOffset={0}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                            onOpenAutoFocus={(e) => e.preventDefault()}
                        >
                            
                            <div className="space-y-0.5">
                                {DOCUMENT_SUBTYPES.map((subtype) => (
                                    <div key={subtype.value}>
                                        <Tooltip
                                            title={subtype.tooltip}
                                            placement="right"
                                            arrow
                                            slotProps={TOOLTIP_SLOT_PROPS}
                                            disableFocusListener
                                            disableTouchListener
                                            enterDelay={400}
                                        >
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleDocSubtypeClick(
                                                        subtype.value
                                                    )
                                                }
                                                className="w-full rounded-md px-3 py-1.5 text-left text-[13px] text-slate-600 hover:bg-blue-50 hover:text-blue-700 transition-colors cursor-pointer focus:outline-none"
                                            >
                                                {subtype.label}
                                            </button>
                                        </Tooltip>
                                    </div>
                                ))}
                            </div>
                        </PopoverContent>
                    </div>
                </Popover>
            )
        }

        if (isPitchDeck) {
            return (
                <Popover
                    key={chip.type}
                    open={pitchPopoverOpen}
                    onOpenChange={setPitchPopoverOpen}
                >
                    <div
                        onMouseEnter={handlePitchMouseEnter}
                        onMouseLeave={handlePitchMouseLeave}
                    >
                        <PopoverTrigger asChild>
                            {chipWithTooltip}
                        </PopoverTrigger>
                        <PopoverContent
                            className="w-56 p-1 shadow-xl border-slate-200"
                            sideOffset={0}
                            onMouseEnter={handlePitchMouseEnter}
                            onMouseLeave={handlePitchMouseLeave}
                            onOpenAutoFocus={(e) => e.preventDefault()}
                        >
                            
                            <div className="space-y-0.5">
                                {PITCH_DECK_SUBTYPES.map((subtype) => (
                                    <button
                                        key={subtype.value}
                                        type="button"
                                        onClick={() =>
                                            handlePitchSubtypeClick(
                                                subtype.value as DeckType
                                            )
                                        }
                                        className="w-full cursor-pointer rounded-md px-2 py-1.5 text-left hover:bg-blue-50 transition-colors  focus:outline-none flex flex-col gap-0.5 group/btn"
                                    >
                                        <span className="text-[13px] font-medium text-slate-700 group-hover/btn:text-blue-700">
                                            {subtype.label}
                                        </span>
                                        <span className="text-[11px] text-slate-500 leading-tight">
                                            {subtype.tooltip}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </PopoverContent>
                    </div>
                </Popover>
            )
        }

        return chipWithTooltip
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="flex items-start justify-center gap-2 sm:gap-4 overflow-x-auto pb-2 sm:overflow-x-visible pt-1 sm:pb-0"
            style={{ scrollbarWidth: "none" }}
        >
            {CHIPS.map((chip, index) => renderChip(chip, index))}
        </motion.div>
    )
}
